/**
 * Admin-only display tree for automatedPreparation.pricingExplanation.
 * Does not reshape customer payloads — ops review only.
 */

import {
  formatCorridorLabel,
  formatPricingEngineLabel,
  formatQuoteMoney,
} from "./quoteOperationalStatus.js";

const MONEY_KEYS = new Set([
  "amount",
  "baseAmount",
  "bubakiiKeep",
  "contributionAmount",
  "customerTotal",
  "estimatedProviderCost",
  "estimatedProviderCostPerM3",
  "finalTotal",
  "grossMargin",
  "jobCost",
  "markupAmount",
  "minimumAmount",
  "minUpliftAmount",
  "operatingRecovery",
  "packingCharge",
  "providerOffer",
  "ratePerM3",
  "ratePerM3Km",
  "recoverableCost",
  "requiredFloor",
  "unitValue",
  "upliftAmount",
]);

const PERCENT_KEYS = new Set([
  "contributionMarginPercent",
  "contributionPercent",
  "marginPercent",
  "markupPercent",
  "targetContributionMarginPercent",
  "truckUtilisationPercent",
]);

const VOLUME_KEYS = new Set(["billableVolumeM3"]);
const KM_KEYS = new Set(["routeKm"]);

const LABELS = {
  accessCharges: "Access charges",
  adjustments: "Adjustments",
  amount: "Amount",
  baseAmount: "Base amount",
  billableSource: "Billable source",
  billableVolumeM3: "Billable volume",
  belowTarget: "Below contribution target",
  bubakiiKeep: "Bubakii keep",
  costSource: "Cost source",
  contributionAmount: "Contribution",
  contributionMarginPercent: "Contribution margin",
  contributionPercent: "Contribution %",
  corridor: "Corridor",
  customerLabel: "Customer label",
  customerTotal: "Customer total",
  description: "Description",
  destinationRegion: "Destination",
  drivers: "Drivers",
  enabled: "Enabled",
  estimatedProviderCost: "Est. provider cost",
  estimatedProviderCostPerM3: "Est. provider cost / m³",
  exclusivePolicyPending: "Exclusive policy pending",
  finalTotal: "Final total",
  floorApplied: "Floor applied",
  grossMargin: "Gross margin",
  helpers: "Helpers",
  id: "Id",
  includedCrew: "Included crew",
  jobCost: "Job cost",
  live: "Live corridor",
  marginPercent: "Gross margin %",
  markupAmount: "Customer markup",
  markupPercent: "Markup %",
  minimumAmount: "Minimum",
  minUpliftAmount: "Shared-load minimum uplift",
  missing: "Missing corridor row",
  operatingRecovery: "Operating recovery",
  providerCutApplied: "Provider cut applied",
  providerOffer: "Advertised driver offer",
  opsLoadUnits: "Ops load units",
  originRegion: "Origin",
  packingCharge: "Packing total",
  packingProductCharges: "Packing products",
  packingRequested: "Packing requested",
  pricingMode: "Pricing mode",
  pricingPolicyVersion: "Policy version",
  pricingProduct: "Pricing product",
  profitability: "Profitability (internal)",
  quantity: "Qty",
  ratePerM3: "Rate / m³",
  ratePerM3Km: "Rate / m³·km",
  recommendedTruckSize: "Recommended truck",
  recoverableCost: "Recoverable cost",
  requiredFloor: "Required floor",
  resourceCharges: "Resource charges",
  reviewReasons: "Review reasons",
  reviewState: "Review state",
  routeKm: "Route",
  ruleKey: "Rule",
  selectedTruckSize: "Selected truck",
  serviceType: "Service",
  settingKey: "Setting",
  sharedMinApplied: "Shared minimum applied",
  splitApplied: "Commercial split",
  targetContributionMarginPercent: "Target contribution",
  truckUtilisationPercent: "Truck utilisation",
  unitValue: "Unit value",
  upliftAmount: "Floor uplift",
};

const PRODUCT_KEYS = [
  "pricingProduct",
  "pricingMode",
  "serviceType",
  "customerLabel",
  "pricingPolicyVersion",
  "reviewState",
];

const VOLUME_SECTION_KEYS = [
  "billableVolumeM3",
  "billableSource",
  "opsLoadUnits",
  "routeKm",
  "ratePerM3",
  "ratePerM3Km",
];

const TRANSPORT_KEYS = [
  "baseAmount",
  "minimumAmount",
  "selectedTruckSize",
  "recommendedTruckSize",
  "truckUtilisationPercent",
  "includedCrew",
];

const KNOWN_ROOT_KEYS = new Set([
  ...PRODUCT_KEYS,
  ...VOLUME_SECTION_KEYS,
  ...TRANSPORT_KEYS,
  "corridor",
  "accessCharges",
  "packingCharge",
  "packingProductCharges",
  "packingRequested",
  "resourceCharges",
  "adjustments",
  "rounding",
  "finalTotal",
  "profitability",
  "reviewReasons",
]);

/**
 * @param {string} key
 * @returns {string}
 */
export function labelPricingExplanationKey(key) {
  if (LABELS[key]) return LABELS[key];
  return String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[._]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * @param {string} key
 * @param {unknown} value
 * @returns {string}
 */
export function formatPricingExplanationValue(key, value) {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";

  if (PERCENT_KEYS.has(key) && Number.isFinite(Number(value))) {
    return `${Number(value)}%`;
  }
  if (VOLUME_KEYS.has(key) && Number.isFinite(Number(value))) {
    return `${Number(value).toFixed(2)} m³`;
  }
  if (KM_KEYS.has(key) && Number.isFinite(Number(value))) {
    return `${Number(value).toLocaleString("en-ZA")} km`;
  }
  if (MONEY_KEYS.has(key) && Number.isFinite(Number(value))) {
    return formatQuoteMoney(value);
  }
  if (key === "pricingProduct" || key === "serviceType") {
    return formatPricingEngineLabel(value);
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return String(value);
}

function isEmptyValue(value) {
  if (value == null || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
}

/**
 * @param {string} key
 * @param {unknown} value
 * @param {string} path
 * @returns {object|null}
 */
function buildNode(key, value, path) {
  if (isEmptyValue(value) && typeof value !== "boolean" && value !== 0) {
    return null;
  }

  if (Array.isArray(value)) {
    const children = value
      .map((item, index) => {
        const childKey = `${key}[${index}]`;
        const childPath = `${path}.${childKey}`;
        if (item != null && typeof item === "object" && !Array.isArray(item)) {
          const label =
            item.description ||
            item.ruleKey ||
            item.code ||
            `${labelPricingExplanationKey(key)} ${index + 1}`;
          const nested = Object.entries(item)
            .map(([nestedKey, nestedValue]) =>
              buildNode(nestedKey, nestedValue, `${childPath}.${nestedKey}`)
            )
            .filter(Boolean);
          return {
            key: childPath,
            label: String(label),
            children: nested,
          };
        }
        return buildNode(childKey, item, childPath);
      })
      .filter(Boolean);

    if (children.length === 0) return null;
    return {
      key: path,
      label: labelPricingExplanationKey(key),
      children,
    };
  }

  if (value != null && typeof value === "object") {
    if (key === "corridor") {
      const corridorLabel = formatCorridorLabel(value);
      const children = Object.entries(value)
        .map(([nestedKey, nestedValue]) =>
          buildNode(nestedKey, nestedValue, `${path}.${nestedKey}`)
        )
        .filter(Boolean);
      return {
        key: path,
        label: "Corridor",
        value: corridorLabel !== "—" ? corridorLabel : undefined,
        children,
      };
    }

    const children = Object.entries(value)
      .map(([nestedKey, nestedValue]) =>
        buildNode(nestedKey, nestedValue, `${path}.${nestedKey}`)
      )
      .filter(Boolean);
    if (children.length === 0) return null;
    return {
      key: path,
      label: labelPricingExplanationKey(key),
      children,
    };
  }

  return {
    key: path,
    label: labelPricingExplanationKey(key),
    value: formatPricingExplanationValue(key, value),
  };
}

function leafFromExplanation(explanation, key) {
  return buildNode(key, explanation[key], key);
}

/**
 * Structured admin tree for a persisted pricingExplanation.
 * Known sections first; any leftover keys are appended so the tree is complete.
 *
 * @param {object|null|undefined} explanation
 * @returns {object[]}
 */
export function buildPricingExplanationTree(explanation) {
  if (!explanation || typeof explanation !== "object") return [];

  const sections = [];

  const productChildren = PRODUCT_KEYS.map((key) =>
    leafFromExplanation(explanation, key)
  ).filter(Boolean);
  if (productChildren.length > 0) {
    sections.push({
      key: "section.product",
      label: "Product",
      children: productChildren,
    });
  }

  const corridorNode = leafFromExplanation(explanation, "corridor");
  if (corridorNode) sections.push(corridorNode);

  const volumeChildren = VOLUME_SECTION_KEYS.map((key) =>
    leafFromExplanation(explanation, key)
  ).filter(Boolean);
  if (volumeChildren.length > 0) {
    sections.push({
      key: "section.volume",
      label: "Volume & distance",
      children: volumeChildren,
    });
  }

  const transportChildren = TRANSPORT_KEYS.map((key) =>
    leafFromExplanation(explanation, key)
  ).filter(Boolean);
  if (transportChildren.length > 0) {
    sections.push({
      key: "section.transport",
      label: "Transport",
      children: transportChildren,
    });
  }

  const accessNode = leafFromExplanation(explanation, "accessCharges");
  if (accessNode) sections.push(accessNode);

  const packingChildren = [
    leafFromExplanation(explanation, "packingRequested"),
    leafFromExplanation(explanation, "packingCharge"),
    leafFromExplanation(explanation, "packingProductCharges"),
  ].filter(Boolean);
  if (packingChildren.length > 0) {
    sections.push({
      key: "section.packing",
      label: "Packing",
      children: packingChildren,
    });
  }

  const resourcesNode = leafFromExplanation(explanation, "resourceCharges");
  if (resourcesNode) sections.push(resourcesNode);

  const adjustmentsNode = leafFromExplanation(explanation, "adjustments");
  if (adjustmentsNode) sections.push(adjustmentsNode);

  const roundingNode = leafFromExplanation(explanation, "rounding");
  if (roundingNode) {
    sections.push({
      key: "section.rounding",
      label: "Rounding",
      children: [roundingNode],
    });
  }

  const profitabilityNode = leafFromExplanation(explanation, "profitability");
  if (profitabilityNode) sections.push(profitabilityNode);

  const reasonsNode = leafFromExplanation(explanation, "reviewReasons");
  if (reasonsNode) sections.push(reasonsNode);

  const totalNode = leafFromExplanation(explanation, "finalTotal");
  if (totalNode) {
    sections.push({
      key: "section.total",
      label: "Total",
      children: [totalNode],
    });
  }

  const leftover = Object.keys(explanation)
    .filter((key) => !KNOWN_ROOT_KEYS.has(key))
    .map((key) => leafFromExplanation(explanation, key))
    .filter(Boolean);
  if (leftover.length > 0) {
    sections.push({
      key: "section.other",
      label: "Other",
      children: leftover,
    });
  }

  return sections;
}
